import { useEffect, useMemo, useRef, useState } from 'react';
import type { CSSProperties, RefObject } from 'react';

interface UseCollapsiblePanelOptions {
  isExpanded: boolean;
  prefersReducedMotion: boolean;
  baseStyle: CSSProperties;
  measureKey: string | number | boolean | null | undefined;
  extraExpandedHeight?: number;
  minExpandedHeight?: number;
  observeResize?: boolean;
  remeasureOnExpandFrame?: boolean;
}

interface UseCollapsiblePanelResult {
  contentRef: RefObject<HTMLDivElement | null>;
  contentHeight: number;
  panelStyle: CSSProperties;
}

export function useCollapsiblePanel({
  isExpanded,
  prefersReducedMotion,
  baseStyle,
  measureKey,
  extraExpandedHeight = 50,
  minExpandedHeight = 0,
  observeResize = false,
  remeasureOnExpandFrame = false,
}: UseCollapsiblePanelOptions): UseCollapsiblePanelResult {
  const contentRef = useRef<HTMLDivElement>(null);
  const [contentHeight, setContentHeight] = useState(0);

  useEffect(() => {
    if (!contentRef.current) {
      return undefined;
    }

    const measureContentHeight = () => {
      if (!contentRef.current) {
        return;
      }

      const measured = contentRef.current.scrollHeight;
      setContentHeight((previous) => (previous === measured ? previous : measured));
    };

    measureContentHeight();

    if (!observeResize || typeof ResizeObserver !== 'function') {
      return undefined;
    }

    const observer = new ResizeObserver(() => {
      measureContentHeight();
    });
    observer.observe(contentRef.current);

    return () => {
      observer.disconnect();
    };
  }, [isExpanded, measureKey, observeResize]);

  useEffect(() => {
    if (!remeasureOnExpandFrame || !isExpanded || !contentRef.current) {
      return undefined;
    }

    const rafId = window.requestAnimationFrame(() => {
      if (!contentRef.current) {
        return;
      }

      const measured = contentRef.current.scrollHeight;
      setContentHeight((previous) => (measured > previous ? measured : previous));
    });

    return () => {
      window.cancelAnimationFrame(rafId);
    };
  }, [contentHeight, isExpanded, remeasureOnExpandFrame]);

  const panelStyle = useMemo<CSSProperties>(() => {
    const expandedHeight = Math.max(contentHeight + extraExpandedHeight, minExpandedHeight);
    const nextStyle: CSSProperties = {
      ...baseStyle,
      maxHeight: isExpanded ? `${expandedHeight}px` : '0px',
      opacity: isExpanded ? 1 : 0,
    };

    if (prefersReducedMotion) {
      return {
        ...nextStyle,
        transition: 'none',
      };
    }

    return nextStyle;
  }, [
    baseStyle,
    contentHeight,
    extraExpandedHeight,
    isExpanded,
    minExpandedHeight,
    prefersReducedMotion,
  ]);

  return {
    contentRef,
    contentHeight,
    panelStyle,
  };
}
